import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import ClinicCard from "@/components/ClinicCard";
import NationalContextSidebar from "@/components/NationalContextSidebar";
import Rubric from "@/components/Rubric";
import { SITE_URL } from "@/lib/site";
import { itemListSchema, articleSchema } from "@/components/schema";
import { SEVILLE_CLINICS } from "@/data/clinics-seville";
import { Helmet } from "react-helmet-async";

const PATH = "/best-veneer-clinics-in-seville";
const TITLE = "Porcelain veneer clinics in Seville — an evaluation";
const DESCRIPTION =
  "Five Seville cosmetic-dentistry practices examined on the same twelve-dimension rubric. Each clinic linked to its own website.";
const PUBLISHED = "2026-04-18";
const MODIFIED = "2026-04-18";

export default function BestVeneerClinicsSeville() {
  const itemList = itemListSchema(
    SEVILLE_CLINICS.map((c) => ({
      name: c.name,
      url: `${SITE_URL}${PATH}#${c.slug}`,
      description: c.profile,
    })),
    PATH,
  );

  const article = articleSchema({
    title: TITLE,
    description: DESCRIPTION,
    path: PATH,
    datePublished: PUBLISHED,
    dateModified: MODIFIED,
  });

  return (
    <>
      <SEO title={TITLE} description={DESCRIPTION} path={PATH} type="article" />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(article)}</script>
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Spain", path: "/best-smile-makeover-clinics-in-spain" },
          { name: "Seville", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Comparison &middot; Seville"
        title="Porcelain veneer clinics in Seville — an evaluation"
        standfirst="Five Seville cosmetic-dentistry practices examined on a single rubric, in the same order, linked to their own websites. An Andalusian market shaped by generational family practices and a smaller cohort of named-practitioner institutes positioned around smile design."
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
        readMinutes={15}
      />

      <div className="container-prose prose-editorial mt-10">
        <h2>Why Seville, as a category</h2>
        <p>
          Seville's cosmetic-dentistry market is meaningfully different
          from Madrid or Barcelona. Long-tenured Andalusian family
          practices dominate the mid-market; a smaller cohort of
          named-practitioner institutes — clinics built around a
          single high-profile clinician's academic profile — sits at
          the aesthetic-dentistry end. International-patient traffic
          is real but smaller in volume than on the Mediterranean
          coast. The rubric surfaces different strengths here than it
          does in the capital markets.
        </p>
        <p>
          This piece examines five practices across that range,
          evaluated on the same rubric.
        </p>

        <h2>The rubric, applied locally</h2>
        <p>
          The full rubric lives on our{" "}
          <Link to="/methodology">methodology page</Link>. Summarised:
        </p>
        <Rubric variant="summary" />
        <p className="mt-6">
          For Seville specifically, the dimensions that differentiate
          most sharply are the named-practitioner versus group-practice
          structure, generational continuity, explicit smile-design
          positioning, and transparency around modern minimal-reduction
          preparation technique.
        </p>

        <h2>The clinics evaluated</h2>
        <p>
          Profiles below follow the same structure — short description,
          dimension-by-dimension snapshot, and an honest statement of
          where the clinic leads and trails on this list. Every
          external link goes to the clinic's own website.
        </p>

        {SEVILLE_CLINICS.map((clinic, i) => (
          <ClinicCard key={clinic.slug} clinic={clinic} position={i + 1} />
        ))}

        <h2>What the framework surfaces, on balance</h2>
        <p>
          On <strong>named-practitioner authority</strong>, Seville
          contains at least one institute where the lead clinician's
          academic profile is foregrounded in the public material in a
          way that is rare elsewhere in Spain. For a reader who
          weights academic credentials, award recognition, and
          publicly-available clinician bios, that detail matters more
          than it might in a larger and noisier market.
        </p>
        <p>
          On <strong>generational continuity</strong>, Seville's
          multi-generational family practices lead. A clinic where the
          founders and their successor generation are both in practice
          is a different kind of operation from a clinic where the
          principal is a single clinician, and the rubric surfaces
          that difference rather than hiding it behind a shared
          category label.
        </p>
        <p>
          On <strong>explicit minimal-reduction positioning</strong>,
          a subset of practices on this list name modern conservative
          preparation technique as distinct from older protocols —
          more useful to a reader than the generic "aesthetic
          dentistry" category language.
        </p>

        <NationalContextSidebar currentCity="Seville" />

        <h2>Related reading</h2>
        <p>
          Readers comparing Seville clinics to clinics in other Spanish
          cities should consult the{" "}
          <Link to="/best-smile-makeover-clinics-in-spain">
            national smile-makeover guide
          </Link>
          . If the shortlist also includes Valencia, see{" "}
          <Link to="/best-veneer-clinics-in-valencia">
            the Valencia evaluation
          </Link>
          ; if Madrid,{" "}
          <Link to="/best-veneer-clinics-in-madrid">
            the Madrid evaluation
          </Link>
          . The vocabulary used in the profiles above is developed in{" "}
          <Link to="/natural-vs-overdone-veneers">
            natural versus overdone veneers
          </Link>
          {" "}and{" "}
          <Link to="/minimal-prep-vs-overprepped-veneers">
            minimal-prep versus over-prepped veneers
          </Link>
          .
        </p>

        <hr className="rule" />

        <div className="mt-10">
          <div className="eyebrow mb-2">Revision log</div>
          <ul className="text-small text-ink/80">
            <li>
              2026-04-18 — First publication. Each clinic's name, site
              and public positioning confirmed against the clinic's own
              website at the time of writing.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}
